import React, { Component } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import './AliasList.css';
import {SessionContext} from './SessionContext.js';
import {Section} from '../Common.js';
let time_ago_in_words = require('time_ago_in_words');

class AliasList extends Component {
  static contextType = SessionContext;

  state = {
    filter: "auto",
    search: "",
    sortCol: "site_name",
    sortDir: "asc",
    aliases: []
  }

  saveListSettings() {
    localStorage.setItem('aliasListSettings', JSON.stringify({
      search: this.state.search, 
      filter: this.state.filter, 
      oldFilter: this.state.oldFilter,
      sortCol: this.state.sortCol,
      sortDir: this.state.sortDir
    }));
  }

  async componentDidMount() {
    this.API = this.context.actions.getAPI();
    this.lastRefreshCounter = this.context.refreshCounter;
    let settings = JSON.parse(localStorage.getItem('aliasListSettings')) || this.state;
    let aliases = await this.API.getUserAliases(settings.filter, settings.search, settings.sortCol, settings.sortDir);
    if (aliases) {
      this.setState({
        filter: settings.filter,
        search: settings.search,
        oldFilter: settings.oldFilter,
        sortCol: settings.sortCol,
        sortDir: settings.sortDir,
        aliases: aliases
      })
    } else {
      toast.error('Error loading alias list');
    }
  }

  componentDidUpdate() {
    // Check if refresh counter changed (indicates auto-refresh triggered)
    if (this.context.refreshCounter !== this.lastRefreshCounter) {
      console.log("AliasList: Auto-refresh triggered, reloading aliases");
      this.lastRefreshCounter = this.context.refreshCounter;
      this.refreshAliases();
    }
  }

  async refreshAliases() {
    let aliases = await this.API.getUserAliases(this.state.filter, this.state.search, this.state.sortCol, this.state.sortDir);
    if (aliases) {
      this.setState({aliases: aliases});
    }
  }

  async filterAliases(filter) {
    let aliases = await this.API.getUserAliases(filter, "", this.state.sortCol, this.state.sortDir);
    this.setState({
      filter: filter,
      search: "",
      oldFilter: "",
      aliases: aliases
    }, this.saveListSettings);
  }

  timer = null;
  async searchAliases(search) {
    if (search === "") {
      return this.clearSearch();
    }
    this.setState({
      search: search,
      oldFilter: this.state.filter !== "custom" ? this.state.filter : this.state.oldFilter,
      filter: "custom"
    }, this.saveListSettings);
    if (this.timer)
      clearTimeout(this.timer);
    this.timer = setTimeout(() => {this.doSearchAliases()}, 500);
  }

  async doSearchAliases() {
    let aliases = await this.API.getUserAliases(this.state.filter, this.state.search, this.state.sortCol, this.state.sortDir);
    this.setState({
      aliases: aliases
    }, this.saveListSettings);
  }

  async clearSearch() {
    let aliases = await this.API.getUserAliases(this.state.oldFilter, "", this.state.sortCol, this.state.sortDir);
    this.setState({
      search: "",
      oldFilter: "",
      filter: this.state.oldFilter,
      aliases: aliases
    }, this.saveListSettings);
  }

  handleRowClick(id) {   
    this.props.history.push(`/aliases/${id}`);
  }

  async handleHeaderClick(col) {
    let dir = col === this.state.sortCol ? (this.state.sortDir === "asc" ? "desc": "asc") : "asc"
    let aliases = await this.API.getUserAliases(this.state.filter, this.state.search, col, dir);
    this.setState({
      sortCol: col,
      sortDir: dir,
      aliases: aliases
    }, this.saveListSettings);
  }

  getSortIcon(col) {
    if (col === this.state.sortCol) {      
      return <span className="text-xs text-primary-600 ml-1">{this.state.sortDir === 'asc' ? '↑' : '↓'}</span>
    }
    return null;
  }

  getRowClassNames(alias) {
    let classes = "track"
    if (!alias.alerts_enabled)
      classes +=  " disabled"
    else if (alias.held_count > 0)
      classes += " held_messages"

    if (alias.hidden)
      classes += " hidden"

    return classes;
  }

  getHeaderLabel(name, sort_col, icon) {
    return <th className="sortCol" title={`Sort by ${name}`}><span onClick={() => {this.handleHeaderClick(sort_col)}}>{icon ?? name} {this.getSortIcon(sort_col)}</span></th>
  }

  render() {
    const aliases = this.state.aliases || [];
    
    let clearButton = null;
    if (this.state.search !== "") {
      clearButton = (
        <button 
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => this.clearSearch()}
          style={{marginLeft: 'var(--space-2)'}}
        >
          Clear
        </button>
      );
    }

    let heldMessage = null;
    if (aliases.length > 0 && aliases.some(a => a.held_count > 0 && a.alerts_enabled === 1)) {
      heldMessage = (
        <div className="card mb-4" style={{borderColor: 'var(--error-500)'}}>
          <div className="card-content p-4" style={{backgroundColor: '#fef2f2', color: 'var(--error-500)'}}>
            <strong><span role="img" aria-label="Warning">⚠️</span> Messages Held:</strong> You have messages being held for review. 
            Please accept or reject them - messages are only held for 72 hours!
          </div>
        </div>
      );
    }

    return (
      <div className="alias-list">
        <Section name="My Aliases">
          {/* Compact Controls */}
          <div className="flex justify-between items-center mb-4 alias-controls-container">
            <div className="flex gap-3">
              <Link className="btn btn-primary btn-sm px-4 new-alias-btn" to="/new-alias">
                <span className="new-alias-full">+ New Alias</span>
                <span className="new-alias-short">+ New</span>
              </Link>
            </div>
            <div className="flex gap-4 items-center alias-controls-right">
              <select 
                className="form-input text-sm py-2 px-3 filter-select"
                style={{width: '160px'}}
                value={this.state.filter} 
                onChange={(e) => this.filterAliases(e.target.value)}
              >
                <option value="auto">Automatic</option>
                <option value="recont">Recent Activity</option>
                <option value="new">Recently Added</option>
                <option value="disabled">Alerts Disabled</option>
                <option value="all">All</option>
                <option value="custom">Custom Search</option>
              </select>
              <div className="flex items-center gap-2 search-container">
                <input 
                  type="text" 
                  className="form-input text-sm py-2 px-3 search-input"
                  style={{width: '200px'}}
                  placeholder="Search aliases..."
                  value={this.state.search} 
                  onChange={(e) => this.searchAliases(e.target.value)}
                  data-mobile-placeholder="Search..."
                />
                {clearButton}
              </div>
            </div>
          </div>

          {heldMessage}

          {/* Sort and Count */}
          <div className="flex justify-between items-center mb-4 alias-list-header">
            <div className="text-sm text-gray-600 font-medium alias-count-desktop">
              {aliases.length} {aliases.length === 1 ? 'alias' : 'aliases'}
            </div>
            <div className="flex items-center gap-3 text-sm alias-list-sort-controls">
              <span className="text-gray-600 text-sm sort-by-label">Sort by:</span>
              <div className="flex gap-2 alias-list-sort-buttons">
                {[
                  {key: 'site_name', label: 'Site'},
                  {key: 'created_on', label: 'Created'},
                  {key: 'last_message', label: 'Activity'},
                  {key: 'message_count', label: 'Messages'}
                ].map(sort => (
                  <button
                    key={sort.key}
                    className={`sort-link ${
                      this.state.sortCol === sort.key 
                        ? 'sort-link-active' 
                        : 'sort-link-inactive'
                    }`}
                    onClick={() => this.handleHeaderClick(sort.key)}
                  >
                    {sort.label} {this.getSortIcon(sort.key)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Alias Compact List */}
          <div className="space-y-2">
            {aliases.map(alias => (
              <div 
                key={alias.id}
                className={`bg-white border border-gray-200 rounded-lg cursor-pointer alias-card ${
                  !alias.alerts_enabled ? 'opacity-60' : ''
                } ${
                  alias.held_count > 0 ? 'border-red-300 bg-red-50' : ''
                } ${
                  alias.hidden ? 'line-through' : ''
                }`}
                style={{padding: '8px 16px 12px 16px'}}
                onClick={() => this.handleRowClick(alias.id)}
              >
                <div className="flex justify-between items-center alias-card-mobile">
                  {/* Left content - title and email only */}
                  <div className="flex-1 pr-4">
                    {/* Title row */}
                    <h4 className="font-semibold text-gray-900 text-sm" style={{margin: 0, marginBottom: '2px'}}>
                      {alias.site_name}
                      {!alias.alerts_enabled && (
                        <span 
                          className="ml-2 inline-flex items-center text-xs font-medium"
                          style={{
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '9999px',
                            padding: '2px 8px'
                          }}
                        >
                          muted
                        </span>
                      )}
                    </h4>
                    
                    {/* Details row - email + dates */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-primary-600 font-medium">
                        {alias.alias}@nslmail.com
                      </div>
                      <div className="text-gray-500 text-xs alias-card-secondary">
                        <span className="font-medium">Created:</span> {new Date(alias.created_on).toDateString().substr(4)}
                      </div>
                      {alias.last_message && (
                        <div className="text-gray-500 text-xs alias-card-secondary">
                          <span className="font-medium">Last activity:</span> {time_ago_in_words(alias.last_message)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop: All counts */}
                  <div className="flex items-center gap-3 text-sm alias-card-counts-desktop">
                    <div className="text-center min-w-10">
                      <div className="font-semibold text-gray-900">{alias.message_count}</div>
                      <div className="text-xs text-gray-500">msgs</div>
                    </div>
                    {alias.spam_count > 0 && (
                      <div className="text-center min-w-10">
                        <div className="font-semibold text-warning-500">{alias.spam_count}</div>
                        <div className="text-xs text-gray-500">spam</div>
                      </div>
                    )}
                    {alias.held_count > 0 && (
                      <div className="text-center min-w-10">
                        <div className="font-semibold text-error-500">{alias.held_count}</div>
                        <div className="text-xs text-gray-500">held</div>
                      </div>
                    )}
                  </div>

                  {/* Mobile: Only held count */}
                  {alias.held_count > 0 && (
                    <div className="text-center min-w-10 alias-card-counts-mobile">
                      <div className="font-semibold text-error-500">{alias.held_count}</div>
                      <div className="text-xs text-gray-500">held</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {aliases.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">No aliases found</div>
              <div className="text-gray-400 mt-2">
                {this.state.search ? 'Try adjusting your search terms' : 'Create your first alias to get started'}
              </div>
              {!this.state.search && (
                <Link className="btn btn-primary mt-4" to="/new-alias">
                  Create Your First Alias
                </Link>
              )}
            </div>
          )}
        </Section>
      </div>
    );
  }

}

export default AliasList;